import { Button, Trash } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NAMESPACE } from '../../../../../locale'
import { riskAssessmentsConstants } from '../../../../../locale/riskAssessments'
import useRiskAssessmentRestore from './hooks/useRiskAssessmentRestore'

interface RiskAssessmentViewFooterProps {
  onEdit: () => void
  hasEditPermission?: boolean
  isHistoryView?: boolean
  archived: boolean
  riskAssessmentId?: string
  residentId?: string
  onRestoreSuccess?: () => void
}

const RiskAssessmentViewFooter: React.FC<RiskAssessmentViewFooterProps> = ({
  onEdit,
  hasEditPermission = true,
  isHistoryView = false,
  archived = false,
  riskAssessmentId,
  residentId,
  onRestoreSuccess
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const riskArchivingFlagEnabled = useFeatureFlags({
    flagNames: ['risk_assessments_archiving']
  })?.data.risk_assessments_archiving.enabled

  const hasRolePermission = useAbilityAccess([
    {
      i: 'update',
      this: 'RiskAssessment'
    }
  ])

  const { restoreRiskAssessment, isLoading } = useRiskAssessmentRestore({
    riskAssessmentId,
    residentId,
    onRestoreSuccess
  })

  if (isHistoryView) {
    return null
  }

  const handleRestoreClick = () => {
    restoreRiskAssessment()
  }

  return (
    <FooterContainer className="lmc-fixed-footer" $archived={archived}>
      {archived && riskArchivingFlagEnabled && (
        <Button
          variant="default"
          onClick={handleRestoreClick}
          disabledByPermission={!hasRolePermission}
          isLoading={isLoading}
        >
          <Trash />
          {t(riskAssessmentsConstants.RESTORE_RISK_ASSESSMENT)}
        </Button>
      )}
      <Button
        variant="primary"
        onClick={onEdit}
        disabledByPermission={!hasEditPermission}
        disabled={archived}
      >
        {t(riskAssessmentsConstants.EDIT)}
      </Button>
    </FooterContainer>
  )
}

const FooterContainer = styled.div<{ $archived: boolean }>`
  width: calc(-290px + 100vw);
  display: flex;
  justify-content: ${(props) =>
    props.$archived ? 'space-between' : 'flex-end'};
  align-items: center;
  padding: 20px;
  background: white;
  border-top: 1px solid #f0f0f0;
`

export default RiskAssessmentViewFooter
